import { Box, Divider, HStack, IconButton, VStack, useDisclosure } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useGetChallengeSolutionQuery } from 'hooks/queries/challenges/useGetChallengeSolutionQuery';
import useConfetti from 'hooks/useConfetti';
import { useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import { BigBadge } from 'components/BigBadge';
import CommentButton from 'components/CommentButton';
import DevSandbox from 'components/DevSandbox';
import DevSandboxSkeleton from 'components/DevSandbox/DevSandboxSkeleton';
import DevSandboxTooltip from 'components/DevSandbox/DevSandboxTooltip';
import BrowserTab from 'components/DevSandbox/PaneTabs/BrowserTab';
import DiscussionTab from 'components/DevSandbox/PaneTabs/Discussion/DiscussionTab';
import RightSideActions from 'components/DevSandbox/RightSideActions';
import Icon from 'components/Icon';
import LikeButton from 'components/LikeButton';
import Link from 'components/Link';
import { ProtectedElement } from 'components/ProtectedElement';
import RenameCodeInput from 'components/RenameCodeInput';
import { SEO } from 'components/SEO';
import SolutionShareButton from 'components/SolutionShareButton';
import Text from 'components/Text';
import ToggleCodePrivacyLock from 'components/ToggleCodePrivacyLock';
import UserAvatar from 'components/UserAvatar';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { Support_Type_Enum } from 'types/databaseEnums';
import { Challenge } from 'types/edgeFunctions';
import { ERoute, TanstackQueryName } from 'types/frontend';
import { identifyRepositoryUrl } from 'utils/solutions';

import ProgressModal from './ProgressModal';
import SolutionsTab from './SolutionsTab';

interface IChallengeSolutionProps {
  challenge: Challenge;
}

const ChallengeSolution = ({
  challenge: { id, title, screen_name, completed_date, solutions_count },
}: IChallengeSolutionProps) => {
  const [codeFrameUrl, setCodeFrameUrl] = useState<string>('');

  const { id: currentUserId, screenName: currentUserScreenName } = useCurrentUser();

  const { isMobile, isSmallScreen } = useMediaQueries();

  const codeEditorRef = useRef<any>(null);

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const progressModal = useDisclosure();

  const hasTabInUrl =
    location.pathname.includes('browser') ||
    location.pathname.includes('comments') ||
    location.pathname.includes('user-solutions');
  const tabScreenName = location.pathname.split('/').pop();
  const solutionUrl = hasTabInUrl
    ? location.pathname.replace(`/${tabScreenName}`, '')
    : location.pathname;
  const browserUrl = hasTabInUrl
    ? window.location.href.replace(`/${tabScreenName}`, '')
    : window.location.href;

  const APP_URL = process.env.REACT_APP_SITE_URL || '';

  const link = `${APP_URL.substring(0, APP_URL.length - 1)}${solutionUrl}`;

  const { data, isPending } = useGetChallengeSolutionQuery({
    solution_url: solutionUrl,
  });

  const isMySolution = Boolean(currentUserId && data?.user.id === currentUserId);

  const Confetti = useConfetti({
    isMySolution,
    solutionUrl,
    solutionId: data?.id || '',
    onComplete: () => progressModal.onOpen(),
  });

  const accountDeleted = data?.user.id === '' || data?.user.id === null;

  if (isPending || !data) {
    return <DevSandboxSkeleton loadingText="Loading challenge solution..." />;
  }

  if (data.is_private && !isMySolution) {
    navigate(`${ERoute.Challenges}/${screen_name}`, { replace: true });
    return null;
  }

  const searchParamsMode = searchParams.get('mode');
  const mode = searchParamsMode === 'full-page' ? 'full-page' : 'interactive';

  const isExternalSolution = identifyRepositoryUrl(data.repository_url);

  return (
    <>
      <SEO
        title={`BigDevSoon | ${data.solution_title} - ${data.title} challenge solution`}
        url={link}
        image={data.screenshot_url}
      />
      <Box position="absolute" zIndex={9999999} top={0} left={0}>
        {Confetti && <Confetti numberOfPieces={500} gravity={0.5} recycle={false} />}
      </Box>
      <DevSandbox
        ref={codeEditorRef}
        id={`challenge-${screen_name}-${currentUserId}-solution-${data.id}`}
        oldKey={data.live_preview_url}
        externalCodeUrl={data.repository_url}
        title={data.solution_title}
        mode={mode}
        initialLayout="preview-only"
        authorEditorSettings={data.editor_settings}
        navigation={{
          leftSide: (
            <HStack pl={1}>
              <VStack spacing={0} alignItems="flex-start">
                <RenameCodeInput
                  title={data.solution_title}
                  isMySolution={isMySolution}
                  type="challenge"
                  shortTitle
                  renameCodeParams={{
                    entityId: data.id,
                    userScreenName: currentUserScreenName,
                    parentScreenName: screen_name,
                    repositoryUrl: data.repository_url || '',
                    previewUrl: data.live_preview_url || '',
                    queryKey: [TanstackQueryName.GetChallengeSolution, solutionUrl],
                    invalidateKeys: [
                      [TanstackQueryName.GetChallengeSolutions, id],
                      [TanstackQueryName.GetUserCode, currentUserId, 0],
                      [TanstackQueryName.GetUserCode, currentUserId, 1],
                      [TanstackQueryName.GetUserCode, currentUserId, 2],
                      [TanstackQueryName.GetProfileCode, data.user.id, 'challenge'],
                      [TanstackQueryName.GetChallengeSolution, solutionUrl],
                    ],
                  }}
                />
                <HStack
                  spacing={4}
                  width="fit-content"
                  cursor={accountDeleted ? 'default' : 'pointer'}
                  alignItems="flex-start"
                >
                  <HStack>
                    {isSmallScreen ? (
                      <UserAvatar
                        key={data.user.id}
                        name={data.user.name}
                        src={data.user.avatar_url}
                        onClick={(e) => {
                          if (accountDeleted) {
                            return;
                          }

                          navigate(`${ERoute.Profile}/${data.user.screen_name}`);
                        }}
                        experience={data.user.xp}
                        size="xs"
                        hideCircularProgress
                      />
                    ) : (
                      <>
                        {!accountDeleted ? (
                          <Link to={`${ERoute.Profile}/${data.user.screen_name}`} color="white">
                            <Text fontSize="micro" maxW="200px" isTruncated opacity="secondary">
                              {data.user.name}
                            </Text>
                          </Link>
                        ) : (
                          <Text fontSize="micro" maxW="200px" isTruncated opacity="secondary">
                            {data.user.name}
                          </Text>
                        )}
                      </>
                    )}
                    <BigBadge plan={data.user.plan} size="small" />
                  </HStack>
                </HStack>
              </VStack>
              <Divider height="24px" orientation="vertical" />
              <HStack spacing={4}>
                <DevSandboxTooltip label="Like">
                  <span>
                    <LikeButton
                      userId={data.user.id}
                      likes={data.likes}
                      likeCodeParams={{
                        entityId: data.id,
                        isLiked: data.is_liked,
                        queryKey: [TanstackQueryName.GetChallengeSolution, solutionUrl],
                        invalidateKeys: [
                          [TanstackQueryName.GetChallengeSolutions, id],
                          [TanstackQueryName.GetUserCode, currentUserId, 0],
                          [TanstackQueryName.GetUserCode, currentUserId, 1],
                          [TanstackQueryName.GetUserCode, currentUserId, 2],
                          [TanstackQueryName.GetProfileCode, data.user.id, 'challenge'],
                        ],
                      }}
                    />
                  </span>
                </DevSandboxTooltip>
                {!isSmallScreen && (
                  <DevSandboxTooltip label="Comments">
                    <span>
                      <CommentButton comments={data.comments_count} url={`${solutionUrl}/comments`} />
                    </span>
                  </DevSandboxTooltip>
                )}
              </HStack>
            </HStack>
          ),
          header: (
            <HStack>
              {isMySolution ? (
                <>
                  <ProtectedElement
                    leftIcon={<Icon type="PencilEdit" />}
                    scheme="outline"
                    to={`${ERoute.Challenges}/${screen_name}`}
                  >
                    Edit
                  </ProtectedElement>
                </>
              ) : (
                <ProtectedElement asButton={false}>
                  <DevSandboxTooltip
                    label={isExternalSolution ? `Can't fork external code` : 'Fork code'}
                  >
                    <IconButton
                      aria-label="fork solution"
                      isDisabled={isExternalSolution}
                      onClick={() => {
                        navigate(`${ERoute.CodeFrames}${ERoute.NewCodeFrame}`, {
                          state: {
                            html: data.html,
                            css: data.css,
                            js: data.js,
                            screenshotUrl: data.screenshot_url,
                            editorSettings: data.editor_settings,
                            shouldCreate: true,
                            isFork: true,
                          },
                        });
                      }}
                    >
                      <Icon type="ForkCode" />
                    </IconButton>
                  </DevSandboxTooltip>
                </ProtectedElement>
              )}
            </HStack>
          ),
          rightSide: (
            <RightSideActions
              leftActions={
                <HStack spacing={2}>
                  {isMySolution && (
                    <ToggleCodePrivacyLock
                      type="challenge"
                      togglePrivacyCodeParams={{
                        isPrivate: data.is_private,
                        entityId: data.id,
                        queryKey: [TanstackQueryName.GetChallengeSolution, solutionUrl],
                        invalidateKeys: [
                          [TanstackQueryName.GetChallengeSolutions, id],
                          [TanstackQueryName.GetUserCode, currentUserId, 0],
                          [TanstackQueryName.GetUserCode, currentUserId, 1],
                          [TanstackQueryName.GetUserCode, currentUserId, 2],
                          [TanstackQueryName.GetProfileCode, data.user.id, 'challenge'],
                        ],
                      }}
                    />
                  )}
                  <SolutionShareButton
                    link={link}
                    isMobile={isMobile}
                    shareText={`Check out ${
                      isMySolution ? 'my latest' : 'this'
                    } solution to the ${title} Challenge on BigDevSoon. 🚀`}
                    emailSubject={`Hey, wanted to share ${
                      isMySolution ? 'my' : 'this'
                    } solution to the ${title} Challenge on BigDevSoon with you`}
                  />
                </HStack>
              }
            />
          ),
        }}
        codeTabs={{
          html: data.html,
          css: data.css,
          js: data.js,
        }}
        paneTabs={{
          tabs: [
            {
              screenName: 'browser',
              title: isExternalSolution ? 'Preview' : 'Browser',
              icon: <Icon type="Browser" size={16} />,
              content: (
                <BrowserTab
                  browserUrl={isExternalSolution ? data.live_preview_url : browserUrl}
                  codeFrameUrl={codeFrameUrl}
                  onCodeFrameUrlChange={codeEditorRef.current?.onCodeFrameUrlChange}
                  screenshotUrl={data.screenshot_url}
                  isExternalSolution={isExternalSolution}
                  repositoryUrl={data.repository_url}
                  previewUrl={data.live_preview_url}
                />
              ),
            },
            {
              title: `${
                data.comments_count > 0 ? `Comments (${data.comments_count})` : 'Comments'
              }`,
              screenName: 'comments',
              icon: <Icon type="CommunityQuestions" />,
              content: (
                <DiscussionTab
                  entityId={data.id}
                  type={Support_Type_Enum.Feedback}
                  isMySolution={isMySolution}
                  refetch={() => {
                    queryClient.invalidateQueries({
                      queryKey: [TanstackQueryName.GetChallengeSolution, solutionUrl],
                    });
                  }}
                />
              ),
            },
            {
              screenName: 'user-solutions',
              title: `${solutions_count > 0 ? `Solutions (${solutions_count})` : 'Solutions'}`,
              icon: <Icon type="SolutionsTab" size={16} />,
              content: <SolutionsTab challengeId={id} />,
            },
          ],
        }}
        callbacks={{
          onCodeFrameUrlChange: (codeFrameUrl) => {
            setCodeFrameUrl(codeFrameUrl);
          },
        }}
        flags={{
          loadCodeFromServer: true,
          isExternalCode: isExternalSolution,
        }}
        footer={{ hideTools: true, showForkHint: !isMySolution && !isExternalSolution }}
      />
      <ProgressModal {...progressModal} challengeTitle={title} completedDate={completed_date!} />
    </>
  );
};

export default ChallengeSolution;
